import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { CreateCommentRequest } from '@/api/schema/data-contracts';
import { ApiCommentType } from '@/api/schema/data-contracts';
import { useApi } from '@/context/api';
import { useAuth } from '@/context/auth';

import type { MutationProps } from '../types';

export const useCommentQueryActions = () => {
  const { auth } = useAuth();
  const {
    commentController: {
      v1CommentsCreate,
      v1EstimationItemsCommentsList,
      v1EstimationsCommentsList,
    },
  } = useApi();

  const queryClient = useQueryClient();

  const useCreateNewComment = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['create-comment'],
      mutationFn: async (createCommentData: CreateCommentRequest) => {
        const { data } = await v1CommentsCreate(createCommentData);
        return data;
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['comments'],
        });
      },
    });

  const useGetComments = (itemId: string, commentType: ApiCommentType) =>
    useQuery({
      enabled: Boolean(auth),
      queryKey: ['comments', commentType, itemId],
      queryFn: async () => {
        switch (commentType) {
          case ApiCommentType.ESTIMATION: {
            const { data } = await v1EstimationsCommentsList(itemId);
            return data;
          }
          case ApiCommentType.ESTIMATION_ITEM: {
            const { data } = await v1EstimationItemsCommentsList(itemId);
            return data;
          }
        }
      },
    });

  return {
    useCreateNewComment,
    useGetComments,
  };
};
